import { Button, Timeline, Tooltip, Form, Modal, Input, Space, Skeleton, Result } from "antd";
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../hooks/redux";
import { declineRequest, downloadDocument, downloadFile, signRequest } from "../../store/api/axiosApi";
import { signularisApi, useGetMetaDocumentQuery } from "../../store/api/signularisApi";
import { RootState } from "../../store/store";
import { documentTemplates } from "../../const";
import { CREATE_DOCUMENT_ROUTE, MAIN_PAGE_ROUTE, META_DOCUMENT_INFO_ROUTE } from "../../routing/consts";
import Generator5 from "../createDocument/generators/generator5";
import style from './metadocumentinfo.module.scss';
import { 
    FileTextOutlined, 
    UserOutlined, 
    ClockCircleOutlined, 
    PaperClipOutlined,
    CheckCircleFilled,
    CloseCircleFilled,
    LinkOutlined,
    ArrowLeftOutlined
} from '@ant-design/icons';

const { TextArea } = Input;

function MetaDocumentInfo() {
    let { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { data: infoData, isLoading, isError, refetch } = useGetMetaDocumentQuery(id!);
    const userInfo = useAppSelector((state: RootState) => state.userinfo);
    const dispatch = useAppDispatch();

    const [modals, setModals] = useState({ sign: false, decline: false, nakladnaya: false });
    const [loading, setLoading] = useState({ sign: false, decline: false });
    const [password, setPassword] = useState("");
    const [comment, setComment] = useState("");

    const handleAction = async (type: 'sign' | 'decline') => {
        setLoading(prev => ({ ...prev, [type]: true }));
        const sigs = infoData?.signatures.filter(s => s.status === 1 || s.status === 2).length || 0;
        
        if (type === 'sign') {
            await signRequest(id!, password, infoData!.fileId, userInfo, sigs, comment);
        } else {
            await declineRequest(id!, password, infoData!.fileId, userInfo, sigs, comment);
        }
        
        dispatch(signularisApi.util.invalidateTags(['MetaDocument']));
        refetch();
        window.location.reload();
    };

    // КРАСИВАЯ ЗАГРУЗКА (SKELETON)
    if (isLoading) return (
        <div className={style.container}>
            <div className={style.content}>
                <div className={style.mainCard}>
                    <Skeleton active title={{ width: '40%' }} paragraph={{ rows: 1, width: '20%' }} />
                    <Skeleton active paragraph={{ rows: 4 }} style={{ marginTop: 40 }} />
                    <div style={{ marginTop: 40, display: 'flex', gap: 12 }}>
                        <Skeleton.Button active size="large" style={{ width: 120 }} />
                        <Skeleton.Button active size="large" style={{ width: 120 }} />
                    </div>
                </div>
            </div>
            <div className={style.sidebar}>
                <div className={style.timelineCard}>
                    <Skeleton active paragraph={{ rows: 5 }} />
                </div>
            </div>
        </div>
    );

    // СООБЩЕНИЕ ОБ ОШИБКЕ / ДОКУМЕНТ НЕ НАЙДЕН
    if (isError || !infoData) return (
        <div className={style.errorWrapper}>
            <Result
                status="404"
                title="Документ не найден"
                subTitle="Извините, запрашиваемый документ не существует или у вас нет прав на его просмотр."
                extra={[
                    <Button type="primary" key="home" icon={<ArrowLeftOutlined />} onClick={() => navigate('/')}>
                        На главную
                    </Button>,
                    <Button key="retry" onClick={() => refetch()}>
                        Обновить
                    </Button>
                ]}
            />
        </div>
    );

    const showUsageButtons = infoData.signatures.find(
        s => s.userFullname === `${userInfo.lastname} ${userInfo.firstname} ${userInfo.middlename}` && s.status === 0
    );

    return (
        <div className={style.container}>
            <div className={style.content}>
                <div className={style.mainCard}>
                    <div className={style.header}>
                        <span className={style.typeBadge}>
                            {documentTemplates[infoData.documentType] || 'Документ'}
                        </span>
                        <h1>{infoData.documentTitle}</h1>
                        <div className={style.meta}>
                            <span><UserOutlined /> {infoData.ownerFullname}</span>
                            <span><ClockCircleOutlined /> {new Date(infoData.time).toLocaleString('ru-RU')}</span>
                        </div>
                    </div>

                    <div className={style.sectionTitle}><FileTextOutlined /> Описание</div>
                    <div className={style.descriptionBox}>
                        {infoData.documentBrief}
                    </div>

                    {(+infoData.reference || isNaN(+infoData.reference)) && (
                        <div style={{ marginTop: 16 }}>
                            <Link to={`${MAIN_PAGE_ROUTE}/${META_DOCUMENT_INFO_ROUTE}/${infoData.reference}`} className="text-blue-500 font-medium flex items-center gap-2">
                                <LinkOutlined /> Ссылается на документ: {infoData.reference}
                            </Link>
                        </div>
                    )}

                    {!!infoData.attachments?.length && (
                        <>
                            <div className={style.sectionTitle}><PaperClipOutlined /> Прикреплённые файлы</div>
                            {infoData.attachments.map((file, idx) => (
                                <div key={idx} className={style.attachmentItem} onClick={() => downloadFile(file)}>
                                    <FileTextOutlined />
                                    <span>{file.split('/').at(-1)}</span>
                                </div>
                            ))}
                        </>
                    )}

                    <div className={style.actionBar}>
                        {showUsageButtons ? (
                            <>
                                <Button type="primary" size="large" onClick={() => setModals(p => ({...p, sign: true}))}>Подписать</Button>
                                <Button danger size="large" onClick={() => setModals(p => ({...p, decline: true}))}>Отклонить</Button>
                            </>
                        ) : infoData.documentType === 'extractStorage' ? (
                            <Button type="primary" size="large" onClick={() => setModals(p => ({...p, nakladnaya: true}))}>Создать накладную</Button>
                        ) : null}
                        <Button size="large" onClick={() => downloadDocument(infoData.fileId)}>Открыть PDF</Button>
                    </div>
                </div>
            </div>

            <div className={style.sidebar}>
                <div className={style.timelineCard}>
                    <div className={style.sectionTitle} style={{ marginTop: 0 }}>Подписанты</div>
                    <Timeline mode="left" style={{ marginTop: 20 }}>
                        {infoData.signatures.map((sig, idx) => (
                            <Timeline.Item 
                                key={idx}
                                dot={
                                    sig.status === 1 ? <CheckCircleFilled style={{ color: '#10b981', fontSize: 16 }} /> :
                                    sig.status === 2 ? <CloseCircleFilled style={{ color: '#ef4444', fontSize: 16 }} /> :
                                    <ClockCircleOutlined style={{ color: '#cbd5e1', fontSize: 16 }} />
                                }
                            >
                                <div style={{ fontSize: 13, fontWeight: 600, color: sig.status === 0 ? '#94a3b8' : '#212d3a' }}>
                                    {sig.userFullname}
                                </div>
                                <div style={{ fontSize: 11, color: '#94a3b8' }}>
                                    {sig.status === 1 ? 'Подписано' : sig.status === 2 ? 'Отклонено' : 'В ожидании'}
                                </div>
                            </Timeline.Item>
                        ))}
                    </Timeline>
                </div>
            </div>

            <Modal
                title="Подписание документа"
                visible={modals.sign}
                onCancel={() => setModals(p => ({...p, sign: false}))}
                onOk={() => handleAction('sign')}
                confirmLoading={loading.sign}
                okText="Подтвердить"
                cancelText="Отмена"
            >
                <TextArea placeholder="Ваш комментарий..." rows={3} style={{ marginBottom: 16 }} onChange={e => setComment(e.target.value)} />
                <Input.Password placeholder="Введите пароль" onChange={e => setPassword(e.target.value)} />
            </Modal>

            <Modal
                title="Отклонение документа"
                visible={modals.decline}
                onCancel={() => setModals(p => ({...p, decline: false}))}
                footer={[
                    <Button key="b" onClick={() => setModals(p => ({...p, decline: false}))}>Отмена</Button>,
                    <Button key="s" type="primary" danger loading={loading.decline} onClick={() => handleAction('decline')}>Отклонить</Button>
                ]}
            >
                <TextArea placeholder="Причина отклонения..." rows={4} style={{ marginBottom: 16 }} onChange={e => setComment(e.target.value)} />
                <Input.Password placeholder="Введите пароль" onChange={e => setPassword(e.target.value)} />
            </Modal>

            <Modal
                title="Создание накладной"
                visible={modals.nakladnaya}
                width={1000}
                onCancel={() => setModals(p => ({...p, nakladnaya: false}))}
                footer={null}
            >
                <Generator5 documentTitle={infoData?.documentTitle} referenceId={infoData.id} referenceType={infoData.documentType} />
            </Modal>
        </div>
    );
}

export default MetaDocumentInfo;
